<!-- src/views/PaysView.vue -->
<template>
  <div class="p-6 max-w-7xl mx-auto">
    <h1 class="text-2xl font-bold mb-6 text-center">CALCULATE PAY INTERFACE</h1>
    
    <!-- Controls -->
    <div class="flex items-center justify-center gap-4 mb-6 flex-wrap">
      <div class="flex items-center gap-2">
        <label class="font-medium">Start Date</label>
        <input type="date" v-model="startDate" class="border rounded px-3 py-1">
      </div>
      <div class="flex items-center gap-2">
        <label class="font-medium">End Date</label>
        <input type="date" v-model="endDate" class="border rounded px-3 py-1">
      </div>
      <button @click="calculatePays" class="bg-primary text-white px-6 py-1 rounded hover:bg-blue-700" :disabled="isBusy">
        {{ isCalculating ? 'Calculating...' : 'Calculate Pays' }}
      </button>
      <button @click="showPays" class="bg-primary text-white px-6 py-1 rounded hover:bg-blue-700" :disabled="isBusy">
        {{ isLoading ? 'Loading...' : 'Display Pays' }}
      </button>
      <button v-if="results && results.length > 0" @click="downloadCSV" class="bg-primary text-white px-6 py-1 rounded hover:bg-blue-700" :disabled="isBusy">
        {{ isLoading ? 'Generating...' : 'Generate Report' }}
      </button>
      <!-- Delete Button -->
      <button v-if="results && results.length > 0 && selectedIds.length > 0" @click="deleteSelectedPays" class="bg-primary text-white px-6 py-1 rounded hover:bg-red-700" :disabled="isDeleting">
        {{ isDeleting ? 'Deleting...' : 'Delete Selected (' + selectedIds.length + ')' }}
      </button>
    </div>
    
    <!-- Totals -->
    <div>
      <span v-if="results && results.length > 0" class="p-2 m-2">Total Hours = {{totalHoursForAllPays}}</span>
      <span v-if="results && results.length > 0" class="p-2 m-2">Total Pay = {{paysTotals}}</span>
    </div>


    <!-- Table -->
    <div class="overflow-x-auto">

    <table class="border-collapse border mx-auto">
      <thead>
        <tr class="bg-warning">
          <th class="border p-2 text-left">
            <input type="checkbox" @change="toggleAll" :checked="selectedIds.length === results.length && results.length > 0">
          </th>
          <th class="border p-2 text-left">Officer</th>
          <th class="border p-2 text-right">Mobile Number</th>
          <th class="border p-2 text-left">Start Date</th>
          <th class="border p-2 text-left">End Date</th>
          <th class="border p-2" style="text-align: right;">Hours</th>
          <th class="border p-2" style="text-align: right;">T.Ex.</th>
          <th class="border p-2" style="text-align: right;">Pay</th>
          <th class="border p-2 text-left">BankAccount</th>
          <th class="border p-2 text-left">Detail</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="!results || results.length === 0">
          <td colspan="10" class="border p-2 text-center text-gray-500">No records found</td>
        </tr>
        <tr v-for="(row, index) in results" :key="index">
          <td class="border p-2 text-left">
            <input type="checkbox" :value="row.payId" v-model="selectedIds">
          </td>
          <td class="border p-2 text-left">{{ row.officerFullNameWithOutID || '-' }}</td>
          <td class="border p-2 text-right">{{ row.officerMobileNumber || '-' }}</td>
          <td class="border p-2">{{ row.startDate || '-' }}</td>
          <td class="border p-2">{{ row.endDate || '-' }}</td>
          <td class="border p-2 text-right" style="text-align: right;">{{ row.totalHours?.toFixed(2) || '0' }}</td>
          <td class="border p-2" style="text-align: right;">{{ row.travelExpense?.toFixed(2) || '0' }}</td>
          <td class="border p-2" style="text-align: right;">{{ row.roundedPay?.toFixed(2) || '-' }}</td>
          <td class="border p-2 text-left">{{ row.bankAccountDetail || '-' }}</td>
          <td class="border p-2 text-left">
            <span @click="showPayDetail(row.payId)" class="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer">
              Detail
            </span>
          </td>
        </tr>
      </tbody>
    </table>

    </div>
  </div>

  <!-- MODAL -->
  <div v-if="showPayDetailModal" style="position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.5);z-index:9999;display:flex;align-items:center;justify-content:center;overflow-y:auto;">
    <div style="background:white;border-radius:8px;max-width:90%;width:1000px;max-height:90vh;overflow-y:auto;margin:20px;">
      <PayDetail 
        :baseUrl="baseUrl" 
        :payId="selectedPayId"
        @close="closePayDetail"
      />
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import PayDetail from '@/components/PayDetail.vue'

const props = defineProps({
  baseUrl: { type: String, required: true }
})

const startDate = ref('')
const endDate = ref('')
const isLoading = ref(false)
const isCalculating = ref(false)
const isBusy = ref(false)
const isDeleting = ref(false)
const paysTotals = ref(0.0)
const totalHoursForAllPays = ref(0.0)
const results = ref([])
const showPayDetailModal = ref(false)
const selectedPayId = ref(null)
const selectedIds = ref([])

const showPayDetail = (payId) => {
  if (!payId) { alert('Invalid pay ID'); return }
  selectedPayId.value = payId
  showPayDetailModal.value = true
}

const closePayDetail = () => {
  showPayDetailModal.value = false
  selectedPayId.value = null
}

const toggleAll = (event) => {
  selectedIds.value = event.target.checked ? results.value.map(r => r.payId) : []
}

const deleteSelectedPays = async () => {
  if (!selectedIds.value.length) { alert('No records selected'); return }
  if (!confirm(`Delete ${selectedIds.value.length} selected pay record(s)?`)) return

  isDeleting.value = true
  isBusy.value = true
  try {
    const response = await fetch(props.baseUrl + "Pays/DeletePays", {
      method: "POST",
      headers: { "Authorization": "Bearer " + localStorage.token, "Content-Type": "application/json" },
      body: JSON.stringify(selectedIds.value)
    })
    if (!response.ok) throw new Error(await response.text())
    const data = await response.json();

    // Remove deleted records from results
    results.value = results.value.filter(r => !selectedIds.value.includes(r.payId))
    selectedIds.value = []
    paysTotals.value = results.value.reduce((sum, item) => sum + (item.roundedPay || 0), 0).toFixed(2);
    totalHoursForAllPays.value = results.pays.reduce((sum, item) => sum + (item.totalHours || 0), 0).toFixed(2);

    alert(data.message || 'Records deleted successfully')
  } catch (e) {
    alert('Failed to delete: ' + e.message)
    console.log('Failed to delete: ' , e.message)
  } finally {
    isDeleting.value = false
    isBusy.value = false
  }
}

const calculatePays = async () => {
  if (!startDate.value || !endDate.value) { alert('Please select both dates'); return }
  if (!localStorage.token) { alert('Please login first'); return }

  isCalculating.value = true
  isBusy.value = true
  try {
    const response = await fetch(props.baseUrl + "Pays/CalculatePays", {
      method: "POST",
      headers: { "Authorization": "Bearer " + localStorage.token, "Content-Type": "application/json" },
      body: JSON.stringify({ startDate: startDate.value, endDate: endDate.value })
    })
    if (!response.ok) throw new Error(await response.text())
    const data = await response.json()
    results.value = data.pays.map(row => ({
      ...row,
      startDate: new Date(row.startDate).toLocaleDateString('en-GB'),
      endDate: new Date(row.endDate).toLocaleDateString('en-GB')
    }))
    paysTotals.value = data.pays.reduce((sum, item) => sum + (item.roundedPay || 0), 0).toFixed(2);
    totalHoursForAllPays.value = data.pays.reduce((sum, item) => sum + (item.totalHours || 0), 0).toFixed(2);

    alert(data.message)
  } catch (e) {
    alert('Failed to calculate pays: ' + e.message)
  } finally {
    isCalculating.value = false
    isBusy.value = false
  }
}

const showPays = async () => {
  if (!startDate.value || !endDate.value) { alert('Please select both dates'); return }
  if (!localStorage.token) { alert('Please login first'); return }

  isLoading.value = true
  isBusy.value = true
  try {
    const response = await fetch(props.baseUrl + "Pays/ShowPays", {
      method: "POST",
      headers: { "Authorization": "Bearer " + localStorage.token, "Content-Type": "application/json" },
      body: JSON.stringify({ startDate: startDate.value, endDate: endDate.value })
    })
    if (!response.ok) throw new Error(await response.text())
    const data = await response.json()
    results.value = data.pays.map(row => ({
      ...row,
      startDate: new Date(row.startDate).toLocaleDateString('en-GB'),
      endDate: new Date(row.endDate).toLocaleDateString('en-GB')
    }))

    paysTotals.value = data.pays.reduce((sum, item) => sum + (item.roundedPay || 0), 0).toFixed(2);
    totalHoursForAllPays.value = data.pays.reduce((sum, item) => sum + (item.totalHours || 0), 0).toFixed(2);

    alert(data.message)
  } catch (e) {
    alert('Failed to load pays: ' + e.message)
  } finally {
    isLoading.value = false
    isBusy.value = false
  }
}

const downloadCSV = () => {
  if (results.value.length === 0) { alert('No data to export.'); return }
  
  const headers = [
    'Officer',
    'Mobile Number',
    'Start Date',
    'End Date',
    'Total Hours',
    'Travel Expense',
    'Pay',
    'Bank Account'
  ]
  
  const rows = results.value.map(row => [
    row.officerFullNameWithOutID || '-',
    `="${row.officerMobileNumber}"` || '-', // keep as plain text
    row.startDate,
    row.endDate,
    Number(row.totalHours || 0).toFixed(2),   // numeric
    Number(row.travelExpense || 0).toFixed(2), // numeric
    Number(row.roundedPay || 0).toFixed(2),    // numeric
    row.bankAccountDetail || '-'
  ])
  
  // Add totals row with formulas
  const totalRows = rows.length + 1
  rows.push([
    'TOTAL',
    '',
    '',
    '',
    `=SUM(E2:E${totalRows})`,
    `=SUM(F2:F${totalRows})`,
    `=SUM(G2:G${totalRows})`,
    ''
  ])
  
  const csvContent = [headers, ...rows].map(r => r.join(',')).join('\n')
  const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = `Pay_Report_${startDate.value}_to_${endDate.value}.csv`
  link.click()
  URL.revokeObjectURL(link.href)
}
</script>